import React, { useRef } from "react"
import PropTypes from "prop-types"

import classnames from "classnames"

import { connectionShape } from "app/lib/shared_prop_types"

import Fittty from "shared/components/fittty"
import NeverEnabledMeta from "./never_enabled_meta"
import EnabledMeta from "./enabled_meta"
import AuthorInfo from "shared/components/connection_card/author_info"
import { iftttAiGradient, iftttAiGradientBackground } from "shared/utils/ifttt_logo"
import { WorksWithContent } from "./works_with_content"

import styles from "./small_applet_card.module.scss"

// className need to be passed manually
// otherwise the transpiler does not detect the properties
const LinkableAppletWrapper = React.forwardRef(({ children, href, onClick, className, ...attributes }, ref) => {
  if (href || onClick) {
    return (
      <a ref={ref} className={`${className} ${styles["applet-card-body"]}`} onClick={onClick} href={href} {...attributes}>
        {children}
      </a>
    )
  } else {
    return (
      <div ref={ref} className={`${className} ${styles["applet-card-body"]}`} {...attributes}>
        {children}
      </div>
    )
  }
})

export const mainServiceOf = applet =>
  applet.channels.find(el => el.module_name === applet.service_slug) || applet.channels[0]

export default function SmallAppletCard(props) {
  const {
    applet,
    appletPath,
    availableToRestore,
    extraClass,
    onAppletClick,
    paused,
    showConnectButton,
    showRestoreButton,
    target,
    useDeactivatedColor,
    position,
    context,
    ...linkProps
  } = props

  const cardRef = useRef(null)

  const mainService = mainServiceOf(applet)
  const backgroundImage =
    applet.background_images?.background_image_url_1x || applet.background_images?.sm_background_image_url
  const disconnectedState = applet.status === "disabled_for_user" || useDeactivatedColor
  // Archived cards use the DSL deactivated gray; status-driven disconnected
  // ones keep the legacy #aaa until the design covers that state.
  const deactivatedGray = useDeactivatedColor ? "#707070" : "#aaa"
  const style = disconnectedState
    ? { backgroundColor: deactivatedGray }
    : { backgroundColor: `#${mainService.brand_color}` }

  // Figma 1818-319795: a paused card shows the brand color 20% lighter.
  const pausedOverlay = paused && "linear-gradient(rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.2))"

  if (backgroundImage) {
    if (disconnectedState) {
      // the deactivated gray with 42% alpha
      const overlay = useDeactivatedColor ? "rgba(112, 112, 112, .58)" : "rgba(170, 170, 170, .58)"
      style.backgroundImage = `linear-gradient(to bottom, ${overlay}, ${overlay}), url('${backgroundImage}')`
    } else {
      style.backgroundImage = `linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(0, 0, 0, 0.4)), url('${backgroundImage}')`
    }
  } else if (!disconnectedState && iftttAiGradient(mainService.module_name)) {
    Object.assign(style, iftttAiGradientBackground("applet_card"))
  }

  if (pausedOverlay) {
    style.backgroundImage = style.backgroundImage ? `${pausedOverlay}, ${style.backgroundImage}` : pausedOverlay
  }

  return (
    <LinkableAppletWrapper
      ref={cardRef}
      style={style}
      className={classnames({
        "applet-card-body": true,
        [`applet-${applet.id}`]: true,
        [`${extraClass}`]: true,
        "applet-card-no-link": !props.appletPath,
        "applet-card-deactivated": useDeactivatedColor,
      })}
      href={appletPath}
      onClick={onAppletClick}
      target={target}
      data-applet-id={applet.id}
      data-applet-owner-type={applet.by_service_owner ? "service" : "user"}
      data-applet-published-type={applet.published ? "published" : "diy"}
      data-applet-author={applet.author?.login}
      data-position={position}
      data-context={context}
      {...linkProps}
    >
      <div className="meta-header">
        <WorksWithContent applet={applet} mainService={mainService} />
      </div>
      <div className="content">
        <div
          className={classnames("title", {
            "with-image": backgroundImage,
          }, {
            [styles["with-image"]]: backgroundImage,
          })}
        >
          <Fittty mode="box" text={applet.name} maxSize={34} />
        </div>
        <AuthorInfo connection={applet} plain />
      </div>
      {!applet.status || applet.status === "never_enabled_for_user" ? (
        <NeverEnabledMeta applet={applet} />
      ) : (
        <EnabledMeta
          applet={applet}
          mainService={mainService}
          href={appletPath}
          onAppletClick={onAppletClick}
          appearDisabled={disconnectedState}
          showConnectButton={showConnectButton}
          paused={paused}
        />
      )}
      {showRestoreButton && (
        <div className="small-applet-restore-button-wrapper">
          <span
            className={classnames(
              "button-tertiary button-outlined button-on-color button-label-h5 restore-cta",
              { disabled: !availableToRestore }
            )}
          >
            {availableToRestore ? "Restore" : "Can't restore"}
          </span>
        </div>
      )}
    </LinkableAppletWrapper>
  )
}

SmallAppletCard.propTypes = {
  applet: connectionShape.isRequired,
  appletPath: PropTypes.string,
  availableToRestore: PropTypes.bool,
  extraClass: PropTypes.string,
  onAppletClick: PropTypes.func,
  paused: PropTypes.bool,
  showConnectButton: PropTypes.bool,
  showRestoreButton: PropTypes.bool,
  target: PropTypes.string,
  term: PropTypes.string,
  useDeactivatedColor: PropTypes.bool,
  position: PropTypes.number,
  context: PropTypes.string,
}

SmallAppletCard.defaultProps = {
  availableToRestore: false,
  extraClass: "",
  onAppletClick: () => {},
  showConnectButton: true,
  showRestoreButton: false,
  useDeactivatedColor: false,
}
